import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * 多标签页状态管理
 * - 点侧边栏/跳转打开页面 → 顶部生成标签
 * - 同一 path 只保留一个标签
 * - 首页(/home)为固定标签，不可关闭
 * - 刷新不保留（不持久化），每次从首页干净开始
 */
export interface TabItem {
  /** 路由完整路径（含 query，作为唯一键） */
  path: string
  /** 标签显示名 */
  title: string
  /** 是否固定标签（不可关闭） */
  affix?: boolean
}

/** 固定标签：首页 */
export const AFFIX_PATHS = ['/home']

export const useTabsStore = defineStore('tabs', () => {
  const tabs = ref<TabItem[]>([{ path: '/home', title: '首页', affix: true }])
  const activePath = ref<string>('/home')

  const hasAffix = (path: string) => AFFIX_PATHS.includes(path)

  /** 打开/激活标签（路由跳转时调用） */
  const openTab = (path: string, title: string) => {
    const exist = tabs.value.find(t => t.path === path)
    if (!exist) {
      tabs.value.push({ path, title, affix: hasAffix(path) })
    }
    activePath.value = path
  }

  /** 关闭标签；若关的是当前标签，自动切到相邻标签 */
  const closeTab = (path: string): string | null => {
    const idx = tabs.value.findIndex(t => t.path === path)
    if (idx < 0) return null
    const target = tabs.value[idx]
    if (!target || target.affix) return null
    tabs.value.splice(idx, 1)
    if (activePath.value === path) {
      const next = tabs.value[Math.min(idx, tabs.value.length - 1)]
      activePath.value = next ? next.path : '/home'
      return activePath.value
    }
    return null
  }

  /** 关闭其他标签（保留固定 + 当前） */
  const closeOthers = (keepPath: string) => {
    tabs.value = tabs.value.filter(t => t.affix || t.path === keepPath)
    if (!tabs.value.some(t => t.path === activePath.value)) {
      activePath.value = keepPath
    }
  }

  /** 关闭某路径左侧的标签（保留固定），返回被关的当前激活路径或 null */
  const closeLeft = (path: string): string | null => {
    const idx = tabs.value.findIndex(t => t.path === path)
    if (idx <= 0) return null
    tabs.value = tabs.value.filter((t, i) => t.affix || i >= idx)
    if (!tabs.value.some(t => t.path === activePath.value)) {
      activePath.value = path
      return path
    }
    return null
  }

  /** 关闭某路径右侧的标签（保留固定），返回被关的当前激活路径或 null */
  const closeRight = (path: string): string | null => {
    const idx = tabs.value.findIndex(t => t.path === path)
    if (idx < 0 || idx === tabs.value.length - 1) return null
    tabs.value = tabs.value.filter((t, i) => t.affix || i <= idx)
    if (!tabs.value.some(t => t.path === activePath.value)) {
      activePath.value = path
      return path
    }
    return null
  }

  /** 关闭全部（仅保留固定标签），返回应跳转的路径 */
  const closeAll = (): string => {
    tabs.value = tabs.value.filter(t => t.affix)
    activePath.value = '/home'
    return activePath.value
  }

  const tabCount = computed(() => tabs.value.length)

  /** 退出登录时清空 */
  const reset = () => {
    tabs.value = [{ path: '/home', title: '首页', affix: true }]
    activePath.value = '/home'
  }

  return { tabs, activePath, openTab, closeTab, closeOthers, closeLeft, closeRight, closeAll, reset, tabCount, hasAffix }
})
